/**
 * Compliance Checkbox Component for Medical On Demand
 *
 * Single checkbox combining MOD-specific compliance acknowledgment
 * with PII confirmation and Terms of Use agreement.
 */

import { AlertCircleIcon, CheckIcon } from 'lucide-react-native';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { Linking } from 'react-native';

import { Checkbox, CheckboxIcon, CheckboxIndicator, CheckboxLabel } from '@/components/ui/checkbox';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { TERMS_OF_USE_URL } from '@/constants/links';
import type { MODFormData } from '@/hooks/useMODInquiryForm';

type ComplianceCheckboxProps = {
  control: Control<MODFormData>;
  errors: FieldErrors<MODFormData>;
  isDisabled?: boolean;
};

export function ComplianceCheckbox({
  control,
  errors,
  isDisabled = false,
}: ComplianceCheckboxProps) {
  return (
    <Controller
      name="complianceAcknowledged"
      control={control}
      render={({ field: { onChange, value }, fieldState, formState }) => {
        const showError =
          !!errors.complianceAcknowledged?.message &&
          (fieldState.isTouched || formState.isSubmitted);

        return (
          <FormControl isInvalid={showError} isDisabled={isDisabled}>
            <Checkbox
              testID="compliance-checkbox"
              className="items-start justify-start gap-3"
              value={value?.toString() || ''}
              onChange={(checked) => onChange(checked)}
              isChecked={value}
              size="sm"
              aria-label="Compliance acknowledgment">
              <CheckboxIndicator>
                <CheckboxIcon as={CheckIcon} />
              </CheckboxIndicator>

              <CheckboxLabel className="-m-1 flex-1">
                <Text size="xs" className="font-normal text-typography-900">
                  I acknowledge I have not been contracted or solicited by any manufacturer or
                  distributor to submit this inquiry. By submitting this inquiry, I confirm this
                  message does not contain patient-identifiable information and I agree to Primum's{' '}
                  <LinkText
                    size="xs"
                    className="font-normal text-typography-900 underline"
                    onPress={() => Linking.openURL(TERMS_OF_USE_URL)}>
                    Terms of Use
                  </LinkText>
                  .*
                </Text>
              </CheckboxLabel>
            </Checkbox>

            {showError && (
              <FormControlError className="mt-2">
                <FormControlErrorIcon as={AlertCircleIcon} />
                <FormControlErrorText size="xs">
                  {errors.complianceAcknowledged?.message}
                </FormControlErrorText>
              </FormControlError>
            )}
          </FormControl>
        );
      }}
    />
  );
}
